import { Vector3 } from '../types/Vector3'
import { UpVectorMode, AlignmentMode, HoverDirection, RotationMode, BoundingBox, FieldEventCallback, FieldParameterChange } from './FieldTypes'

export class FieldParameters {
  // ==================== TOPOLOGY PARAMETERS ====================
  
  // Basic geometry
  public radius: number = 5
  public cellSize: number = 2        // Universal cell size (topology-independent)
  public cellSpacing: number = 0     // Padding between cells (can be negative)
  public gridSize: number = 5
  public height: number = 10
  public turns: number = 2
  
  // Start/end modifications
  public startRadius: number = 5
  public endRadius: number = 5
  public startHeight: number = 0
  public endHeight: number = 10
  
  // ==================== ANIMATION PARAMETERS ====================
  
  // Time and frequency
  public time: number = 0
  public hoverFrequency: number = 1
  public rotateFrequency: number = 0.5
  
  // Phase shifts (create wave propagation)
  public hoverPhaseShift: number = 0
  public rotatePhaseShift: number = 0
  
  // Amplitudes (strength of effects)
  public hoverAmplitude: number = 1
  public rotateAmplitude: number = 0.5
  public scaleAmplitude: number = 0.1
  
  // ==================== DIRECTION PARAMETERS ====================
  
  // Orientation modes
  public upVectorMode: UpVectorMode = UpVectorMode.WorldUp
  public alignmentMode: AlignmentMode = AlignmentMode.Aligned
  public hoverDirection: HoverDirection = HoverDirection.Transversal
  public rotationMode: RotationMode = RotationMode.Infinite
  
  // NEW: Manual hover direction override
  public hoverDirectionOverride: 'none' | 'vertical' | 'horizontal' = 'none'
  
  // Custom vectors
  public fieldDirection: Vector3 = new Vector3(0, 0, 1)      // Main field direction (line direction or plane normal)
  public customUpVector: Vector3 = new Vector3(0, 1, 0)
  public customAlignmentVector: Vector3 = new Vector3(0, 0, 1)
  
  // ==================== SPACING PARAMETERS ====================
  
  // Object-based spacing
  public useObjectSpacing: boolean = false
  public objectBounds?: BoundingBox
  public spacingMultiplier: number = 1.2
  
  // ==================== ADVANCED ANIMATION ====================
  
  // Organic motion
  public organicVariation: number = 0.3
  public windStrength: number = 0.5
  public breathingIntensity: number = 0.0
  
  // Wave effects
  public waveSpeed: number = 1.0
  public rippleFrequency: number = 2.0
  public turbulence: number = 0.1
  
  // ==================== EVENT SYSTEM ====================
  
  private _eventCallbacks: FieldEventCallback[] = []
  private _parameterHistory: FieldParameterChange[] = []
  
  // ==================== CONSTRUCTOR ====================
  
  constructor(initialParams?: Partial<FieldParameters>) {
    if (initialParams) {
      this.updateParameters(initialParams)
    }
  }
  
  // ==================== PARAMETER UPDATES ====================
  
  /**
   * Update multiple parameters at once with change tracking
   */
  public updateParameters(newParams: Partial<FieldParameters>): void {
    const changes: FieldParameterChange[] = []
    
    for (const [key, value] of Object.entries(newParams)) {
      if (key.startsWith('_')) continue // Skip private properties
      
      const oldValue = (this as any)[key]
      if (oldValue !== value) {
        changes.push({
          parameterName: key,
          oldValue,
          newValue: value,
          timestamp: Date.now()
        })
        
        ;(this as any)[key] = value
      }
    }
    
    // Notify callbacks about changes
    for (const change of changes) {
      this._parameterHistory.push(change)
      this._eventCallbacks.forEach(callback => callback(change))
    }
  }
  
  /**
   * Update single parameter with change tracking
   */
  public updateParameter(paramName: string, value: any): void {
    this.updateParameters({ [paramName]: value })
  }
  
  // ==================== LIVE ANIMATION ====================
  
  /**
   * Update time-based parameters for live animation
   */
  public updateTime(deltaTime: number): void {
    this.time += deltaTime
    
    // Update any time-dependent parameters
    this.updateDynamicParameters()
  }
  
  /**
   * Update dynamic parameters based on time
   */
  private updateDynamicParameters(): void {
    // Example: Organic phase shift variation
    if (this.organicVariation > 0) {
      const organicShift = Math.sin(this.time * 0.3) * this.organicVariation
      this.hoverPhaseShift = organicShift
    }
    
    // Example: Wind effect
    if (this.windStrength > 0) {
      const windShift = this.time * this.waveSpeed
      this.rotatePhaseShift = windShift
    }
  }
  
  // ==================== PRESETS ====================
  
  /**
   * Apply breathing effect preset
   */
  public applyBreathingPreset(): void {
    this.updateParameters({
      hoverDirection: HoverDirection.Transversal,
      hoverFrequency: 0.5,
      hoverAmplitude: 2,
      hoverPhaseShift: 0,
      breathingIntensity: 0.0 // DISABLED for testing
    })
  }
  
  /**
   * Apply wind effect preset
   */
  public applyWindPreset(): void {
    this.updateParameters({
      hoverDirection: HoverDirection.Longitudinal,
      rotateFrequency: 1.0,
      rotateAmplitude: 1.0,
      windStrength: 1.0,
      waveSpeed: 2.0
    })
  }
  
  /**
   * Apply ripple effect preset
   */
  public applyRipplePreset(): void {
    this.updateParameters({
      hoverDirection: HoverDirection.Transversal,
      hoverFrequency: 2.0,
      hoverAmplitude: 1.5,
      rippleFrequency: 3.0,
      waveSpeed: 1.5
    })
  }
  
    /**
   * Apply snake movement preset
   */
  public applySnakePreset(): void {
    this.updateParameters({
      hoverDirection: HoverDirection.Longitudinal,
      hoverFrequency: 1.5,
      hoverAmplitude: 3.0,
      hoverPhaseShift: Math.PI / 2,
      waveSpeed: 1.0
    })
  }
  
  // ==================== EVENT MANAGEMENT ====================
  
  /**
   * Subscribe to parameter changes
   */
  public onParameterChange(callback: FieldEventCallback): void {
    this._eventCallbacks.push(callback)
  }
  
  /**
   * Unsubscribe from parameter changes
   */
  public offParameterChange(callback: FieldEventCallback): void {
    const index = this._eventCallbacks.indexOf(callback)
    if (index >= 0) {
      this._eventCallbacks.splice(index, 1)
    }
  }
  
  /**
   * Get parameter change history
   */
  public getParameterHistory(): FieldParameterChange[] {
    return [...this._parameterHistory]
  }
  
  /**
   * Clear parameter history
   */
  public clearParameterHistory(): void {
    this._parameterHistory = []
  }
  
  
  /**
   * Validate parameter values
   */
  public validate(): string[] {
    const errors: string[] = []
    
    if (this.radius <= 0) errors.push('Radius must be positive')
    if (this.cellSize <= 0) errors.push('Cell size must be positive')
    if (this.gridSize <= 0) errors.push('Grid size must be positive')
    if (this.hoverFrequency < 0) errors.push('Hover frequency cannot be negative')
    if (this.rotateFrequency < 0) errors.push('Rotate frequency cannot be negative')
    
    return errors
  }
  
  /**
   * Check if parameters are valid
   */
  public isValid(): boolean {
    return this.validate().length === 0
  }
} 